import { useCallback, useEffect, useRef, useState } from "react"
import { useLoadingIndicator } from "modules/indicators"
import { useGetData, usePostData, usePutData } from "services/sales"
import { handleResponse } from "utils/fetch"

export const useSchemes = () => {
  const countRef = useRef()
  const [categories, setCategories] = useState([]) // TODO keep it in context
  const withLoadingIndicator = useLoadingIndicator()
  const fetchData = useGetData("/tw/api/discount/get-discount-info")
  const queryData = usePostData("/tw/api/discount/get-discount-list")
  const createData = usePostData("/tw/api/discount/post-discount")
  const updateData = usePutData("/tw/api/discount/post-discount")
  const fetchCategories = useCallback(
    () =>
      fetchData()
        .then(handleResponse)
        .then((response) => setCategories(response.Data.List)),
    [fetchData],
  )
  const querySchemes = useCallback(
    (query) =>
      queryData(query)
        .then(handleResponse)
        .then((response) => {
          countRef.current = response.Data.TotalCount
          return response.Data.List
        }),
    [queryData],
  )

  const createScheme = useCallback(
    (scheme) => {
      return createData(scheme).then(handleResponse)
    },
    [createData],
  )

  const updateScheme = useCallback(
    (scheme) => {
      return updateData(scheme).then(handleResponse)
    },
    [updateData],
  )

  useEffect(() => {
    withLoadingIndicator(fetchCategories())
  }, [fetchCategories, withLoadingIndicator])

  return { categories, querySchemes, totalCount: countRef.current, createScheme, updateScheme }
}

export const useScheme = (schemeId) => {
  const postData = usePostData("/tw/api/discount/import-discount-coupons")
  const importCoupons = useCallback(
    (serials) => {
      return postData({
        DiscountPlanOptionsId: schemeId,
        CouponCode: serials,
      })
        .then(handleResponse)
        .then((response) => response.Data)
    },
    [postData, schemeId],
  )

  return { importCoupons }
}

export const PROMOTION_TYPE_MANUAL_ACTIVITY = 105 // 手選活動
export const PROMOTION_TYPE_SYSTEM = 112 // 系統活動

export const TELECOM_GROUPS = [
  {
    key: "FET",
    group: 1,
    name: "遠傳電信",
    shortName: "遠傳",
    channelName: "O",
    pattern: /(^EVT[0-9A-Za-z]{12})/i,
    maxLength: 15,
  },
  {
    key: "TWM",
    group: 2,
    name: "台灣大哥大",
    shortName: "台哥大",
    channelName: "Q",
    pattern: /(^(E|N)VT[0-9A-Za-z]{12})/i,
    maxLength: 15,
  },
  {
    key: "CHT",
    group: 3,
    name: "中華電信",
    shortName: "中華",
    channelName: "R",
    pattern: /(^[0-9A-Za-z]{10})/i,
    maxLength: 10,
  },
]

const getYearCodeForRegex = ({ useTwoDigit = false } = {}) => {
  const currentYear = new Date().getFullYear()
  // allow last year
  const lastYear = currentYear - 1
  return useTwoDigit
    ? `(${currentYear.toString().slice(-2)}|${lastYear.toString().slice(-2)})`
    : `(${currentYear}|${lastYear})`
}

export const DISCOUNT_RULES = {
  EVT019: {
    messageIdentity: "員購卡",
    pattern: new RegExp(`^${getYearCodeForRegex()}[0-9]{12}`),
    maxLength: 16,
  },
  EVT020: {
    messageIdentity: "親友卡",
    pattern: /(^B[0-9]{1,8})/i,
    maxLength: 9,
  },
  EVT078: {
    messageIdentity: "媒體公關卡",
    pattern: new RegExp(`^${getYearCodeForRegex()}[0-9]{6}`),
    maxLength: 10,
  },
  EVT170: {
    messageIdentity: "折價券",
    pattern: /(^EVT17[0-9]{4})/i,
    maxLength: 9,
  },
  EVT189: {
    messageIdentity: "",
    pattern: /(^S19[0-9]{9})/i,
    maxLength: 12,
  },
  EVT197: {
    messageIdentity: "取貨聯",
    pattern: /(^[0-9]{14})/i,
    maxLength: 14,
  },
  EVT251: {
    messageIdentity: "",
    pattern: /(^ESG[0-9]{3}809$)/i,
    maxLength: 9,
  },
  EVT252: {
    messageIdentity: "",
    pattern: /(^ESG[0-9]{3}809$)/i,
    maxLength: 9,
  },
  EVT303: {
    messageIdentity: "無印良品發票",
    pattern: /(^[A-Z]{2}-\d{8}$)/i,
    maxLength: 11,
  },
  EVT311: {
    messageIdentity: "",
    pattern: /(^GGR\d{5}$)/i,
    maxLength: 8,
  },
  EVT400: {
    messageIdentity: "Online Store 訂單",
    pattern: /(^GOS\d{4,}$)/i,
    maxLength: null,
  },
  EVT406: {
    messageIdentity: "Online Store 訂單",
    pattern: /(^GOS\d{4,}$)/i,
    maxLength: null,
  },
  EVT407: {
    messageIdentity: "Online Store 訂單",
    pattern: /(^GOS\d{4,}$)/i,
    maxLength: null,
  },
  EVT412: {
    messageIdentity: "大量購車9折專案",
    pattern: new RegExp(`^${getYearCodeForRegex({ useTwoDigit: true })}[01]\\d[A-Z]{2}\\d{5}$`),
    maxLength: null,
  },
  EVT413: {
    messageIdentity: "大量購車95折專案",
    pattern: new RegExp(`^${getYearCodeForRegex({ useTwoDigit: true })}[01]\\d[A-Z]{2}\\d{5}$`),
    maxLength: null,
  },
  EVT414: {
    messageIdentity: "大量購車97折專案",
    pattern: new RegExp(`^${getYearCodeForRegex({ useTwoDigit: true })}[01]\\d[A-Z]{2}\\d{5}$`),
    maxLength: null,
  },
  EVT415: {
    messageIdentity: "大量購車98折專案",
    pattern: new RegExp(`^${getYearCodeForRegex({ useTwoDigit: true })}[01]\\d[A-Z]{2}\\d{5}$`),
    maxLength: null,
  },
  EVT416: {
    messageIdentity: "企業員購指定車款9折專案",
    pattern: /^\S.*/i,
    maxLength: null,
  },
  EVT426: {
    messageIdentity: "AR領牌展示車8折",
    pattern: new RegExp(`^${getYearCodeForRegex({ useTwoDigit: true })}[01]\\dAR\\d{5}$`),
    maxLength: null,
  },
  EVT436: {
    messageIdentity: " Gogoro 購車夢想金折價",
    pattern: /^[01]\dG90[\dA-Z]{5}$/i,
    maxLength: 10,
  },
  EVT437: {
    messageIdentity: " Gogoro 購車夢想金折價",
    pattern: /^[01]\dG24[\dA-Z]{5}$/i,
    maxLength: 10,
  },
  EVT438: {
    messageIdentity: " Gogoro 購車夢想金折價",
    pattern: /^[01]\dG34[\dA-Z]{5}$/i,
    maxLength: 10,
  },
  EVT439: {
    messageIdentity: " Gogoro 購車夢想金折價",
    pattern: /^[01]\dG44[\dA-Z]{5}$/i,
    maxLength: 10,
  },
  EVT453: {
    messageIdentity: "",
    pattern: /^21[A-Za-z0-9]{8}[A-Z]{3}$/,
    maxLength: 13,
  },
  EVT454: {
    messageIdentity: "",
    pattern: /^21[A-Za-z0-9]{8}[A-Z]{3}$/,
    maxLength: 13,
  },
  EVT459: {
    messageIdentity: "外送夥伴S3購車優惠專案",
    pattern: /^[A-Z]+$/i,
    maxLength: null,
  },
  EVT490: {
    messageIdentity: "台塑員工購車專案-S3系列92折",
    pattern: /^N\d{9}$|^[A|B]\d{6}$/,
    maxLength: 10,
  },
  EVT491: {
    messageIdentity: "台塑員工購車專案-指定車款95折",
    pattern: /^N\d{9}$|^[A|B]\d{6}$/,
    maxLength: 10,
  },
  EVT620: {
    messageIdentity: "Gogoro 購車夢想金折價",
    pattern: /^\d{2}G06[A-Za-z\d]{5}$/,
    maxLength: 10,
  },
  EVT621: {
    messageIdentity: "Gogoro 購車夢想金折價",
    pattern: /^\d{2}G15[A-Za-z\d]{5}$/,
    maxLength: 10,
  },
  EVT622: {
    messageIdentity: "Gogoro 購車夢想金折價",
    pattern: /^\d{2}G24[A-Za-z\d]{5}$/,
    maxLength: 10,
  },
  EVT623: {
    messageIdentity: "Gogoro 購車夢想金折價",
    pattern: /^\d{2}G36[A-Za-z\d]{5}$/,
    maxLength: 10,
  },
  EVT645: {
    messageIdentity: "校園大使 夢想購車金折抵100元",
    pattern: /^\d{4}G100G\d{3}$/,
    maxLength: 12,
  },
  EVT646: {
    messageIdentity: "校園大使 夢想購車金折抵200元",
    pattern: /^\d{4}G200G\d{3}$/,
    maxLength: 12,
  },
  EVT647: {
    messageIdentity: "校園大使 夢想購車金折抵300元",
    pattern: /^\d{4}G300G\d{3}$/,
    maxLength: 12,
  },
  EVT648: {
    messageIdentity: "校園大使 夢想購車金折抵400元",
    pattern: /^\d{4}G400G\d{3}$/,
    maxLength: 12,
  },
  EVT649: {
    messageIdentity: "校園大使 夢想購車金折抵500元",
    pattern: /^\d{4}G500G\d{3}$/,
    maxLength: 12,
  },
  EVT650: {
    messageIdentity: "校園大使 夢想購車金折抵600元",
    pattern: /^\d{4}G600G\d{3}$/,
    maxLength: 12,
  },
  EVT651: {
    messageIdentity: "校園大使 夢想購車金折抵700元",
    pattern: /^\d{4}G700G\d{3}$/,
    maxLength: 12,
  },
  EVT652: {
    messageIdentity: "校園大使 夢想購車金折抵800元",
    pattern: /^\d{4}G800G\d{3}$/,
    maxLength: 12,
  },
  EVT653: {
    messageIdentity: "校園大使 夢想購車金折抵900元",
    pattern: /^\d{4}G900G\d{3}$/,
    maxLength: 12,
  },
  EVT654: {
    messageIdentity: "校園大使 夢想購車金折抵1000元",
    pattern: /^\d{4}G1000G\d{3}$/,
    maxLength: 13,
  },
  EVT655: {
    messageIdentity: "校園大使 夢想購車金折抵2200元",
    pattern: /^\d{4}G2200G\d{3}$/,
    maxLength: 13,
  },
  EVT656: {
    messageIdentity: "校園大使 夢想購車金折抵2400元",
    pattern: /^\d{4}G2400G\d{3}$/,
    maxLength: 13,
  },
  EVT657: {
    messageIdentity: "校園大使 夢想購車金折抵2600元",
    pattern: /^\d{4}G2600G\d{3}$/,
    maxLength: 13,
  },
  EVT658: {
    messageIdentity: "校園大使 夢想購車金折抵2800元",
    pattern: /^\d{4}G2800G\d{3}$/,
    maxLength: 13,
  },
  EVT659: {
    messageIdentity: "校園大使 夢想購車金折抵3000元",
    pattern: /^\d{4}G3000G\d{3}$/,
    maxLength: 13,
  },
  EVT660: {
    messageIdentity: "校園大使 夢想購車金折抵3200元",
    pattern: /^\d{4}G3200G\d{3}$/,
    maxLength: 13,
  },
  EVT661: {
    messageIdentity: "校園大使 夢想購車金折抵3400元",
    pattern: /^\d{4}G3400G\d{3}$/,
    maxLength: 13,
  },
  EVT662: {
    messageIdentity: "校園大使 夢想購車金折抵3600元",
    pattern: /^\d{4}G3600G\d{3}$/,
    maxLength: 13,
  },
  EVT663: {
    messageIdentity: "校園大使 夢想購車金折抵3800元",
    pattern: /^\d{4}G3800G\d{3}$/,
    maxLength: 13,
  },
  EVT664: {
    messageIdentity: "校園大使 夢想購車金折抵4000元",
    pattern: /^\d{4}G4000G\d{3}$/,
    maxLength: 13,
  },
  EVT665: {
    messageIdentity: "校園大使 夢想購車金折抵4200元",
    pattern: /^\d{4}G4200G\d{3}$/,
    maxLength: 13,
  },
  EVT666: {
    messageIdentity: "校園大使 夢想購車金折抵4400元",
    pattern: /^\d{4}G4400G\d{3}$/,
    maxLength: 13,
  },
  EVT667: {
    messageIdentity: "校園大使 夢想購車金折抵4600元",
    pattern: /^\d{4}G4600G\d{3}$/,
    maxLength: 13,
  },
  EVT668: {
    messageIdentity: "校園大使 夢想購車金折抵4800元",
    pattern: /^\d{4}G4800G\d{3}$/,
    maxLength: 13,
  },
  EVT669: {
    messageIdentity: "校園大使 夢想購車金折抵5000元",
    pattern: /^\d{4}G5000G\d{3}$/,
    maxLength: 13,
  },
  EVT670: {
    messageIdentity: "校園大使 夢想購車金折抵5200元",
    pattern: /^\d{4}G5200G\d{3}$/,
    maxLength: 13,
  },
  EVT671: {
    messageIdentity: "校園大使 夢想購車金折抵5400元",
    pattern: /^\d{4}G5400G\d{3}$/,
    maxLength: 13,
  },
  EVT672: {
    messageIdentity: "校園大使 夢想購車金折抵5600元",
    pattern: /^\d{4}G5600G\d{3}$/,
    maxLength: 13,
  },
  EVT673: {
    messageIdentity: "校園大使 夢想購車金折抵5800元",
    pattern: /^\d{4}G5800G\d{3}$/,
    maxLength: 13,
  },
  EVT674: {
    messageIdentity: "校園大使 夢想購車金折抵6000元",
    pattern: /^\d{4}G6000G\d{3}$/,
    maxLength: 13,
  },
  EVT676: {
    messageIdentity: "員購92折-親友限定分享專案",
    pattern: /^\d{2}[\dA-Z]{8}[A-Z]{3}$/,
    maxLength: 13,
  },
  EVT704: {
    messageIdentity: "全聯購車訂金$2988",
    pattern: /^[09]{2}[\d]{8}$/,
    maxLength: 10,
  },
  EVT714: {
    messageIdentity: "2022歡慶 Gogoro Rewards 上市 員工親友加碼 92折",
    pattern: /^\d{2}GO[\dA-Z]{8}[A-Z]{3}$/,
    maxLength: 15,
  },
  EVT715: {
    messageIdentity: "台新Richart購車定存現折1029",
    pattern: /^0210G29[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT716: {
    messageIdentity: "台新Richart購車定存現折1027",
    pattern: /^0310G27[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT717: {
    messageIdentity: "台新Richart購車定存現折1018",
    pattern: /^0410G18[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT718: {
    messageIdentity: "台新Richart購車定存現折1044",
    pattern: /^0510G44[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT719: {
    messageIdentity: "台新Richart購車定存現折515",
    pattern: /^0205G15[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT720: {
    messageIdentity: "台新Richart購車定存現折514",
    pattern: /^0305G14[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT721: {
    messageIdentity: "台新Richart購車定存現折509",
    pattern: /^0405G09[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  EVT722: {
    messageIdentity: "台新Richart購車定存現折522",
    pattern: /^0505G22[\dA-Za-z]{3}$/,
    maxLength: 10,
  },
  DEFAULT: {
    messageIdentity: "",
    pattern: null,
    maxLength: null,
  },
}
